import React, { useState, useMemo } from 'react';
import { ArrowForwardIos as RightIcon } from '@mui/icons-material';
import dayjs from 'dayjs';

export default function MiniCalendar({
  onDateSelect,
  onMonthChange,
  calendarEvents = [],
}) {
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(today);
  const [selectedDate, setSelectedDate] = useState(today);
  const changeMonth = (offset) => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + offset,
      1
    );
    setCurrentDate(newDate);
    // ★ 중요: 부모에게 바뀐 월의 첫 번째 날짜를 전달하여 서버 조회를 유도
    onMonthChange?.(newDate);
  };
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const lastDate = new Date(year, month + 1, 0).getDate();
    const prevLastDate = new Date(year, month, 0).getDate();

    const days = [];
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        day: prevLastDate - i,
        type: 'prev',
        date: new Date(year, month - 1, prevLastDate - i),
        dayOfWeek: new Date(year, month - 1, prevLastDate - i).getDay(),
      });
    }
    for (let i = 1; i <= lastDate; i++) {
      days.push({
        day: i,
        type: 'current',
        date: new Date(year, month, i),
        dayOfWeek: new Date(year, month, i).getDay(),
      });
    }
    const nextFill = 35 - days.length;
    for (let i = 1; i <= nextFill; i++) {
      days.push({
        day: i,
        type: 'next',
        date: new Date(year, month + 1, i),
        dayOfWeek: new Date(year, month + 1, i).getDay(),
      });
    }
    return days.slice(0, 35);
  }, [currentDate]);

  const hasEvent = (date) => {
    return calendarEvents.some((item) => {
      if (!item?.startDt) return false;
      return dayjs(item.startDt).isSame(dayjs(date), 'day');
    });
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    onDateSelect?.(date);
  };

  const handleTodayClick = () => {
    const now = new Date();
    setCurrentDate(new Date(now.getFullYear(), now.getMonth(), 1));
    setSelectedDate(now);
    onDateSelect?.(now);
  };

  return (
    <div className="xl:col-span-5 border border-[#e1e8ed] rounded-[15px] py-2 px-3 bg-gray-50/30 flex flex-col h-[243px]">
      <div className="flex items-center justify-between mb-1 shrink-0">
        <div className="flex items-center gap-1">
          <button
            onClick={() => changeMonth(-1)}
            className="p-1 hover:bg-gray-200 rounded text-black"
          >
            <RightIcon className="rotate-180 text-[10px]!" />
          </button>
          <span className="font-bold text-sm text-[#2c3e50]">
            {currentDate.getFullYear()}년{' '}
            {String(currentDate.getMonth() + 1).padStart(2, '0')}월
          </span>
          <button
            onClick={() => changeMonth(1)}
            className="p-1 hover:bg-gray-200 rounded text-black"
          >
            <RightIcon className="text-[10px]!" />
          </button>
        </div>

        <button
          onClick={() => {
            const now = new Date();
            handleTodayClick();
            onMonthChange?.(now);
          }}
          className="py-1 px-2 bg-gray-100 cursor-pointer hover:bg-gray-200 rounded text-gray-500 text-xs"
        >
          오늘
        </button>
      </div>

      <div className="grid grid-cols-7 text-[10px] text-center font-bold mb-0.5 shrink-0">
        {['일', '월', '화', '수', '목', '금', '토'].map((d, i) => (
          <span key={d} className={i === 0 ? 'text-red-500' : 'text-[#7f8c8d]'}>
            {d}
          </span>
        ))}
      </div>

      <div className="flex-1 grid grid-cols-7 grid-rows-5 text-xs text-center">
        {calendarDays.map((item, i) => {
          const isSelected =
            selectedDate.toDateString() === item.date.toDateString();
          const isToday = today.toDateString() === item.date.toDateString();
          const isSunday = item.dayOfWeek === 0;
          const showDot = hasEvent(item.date);

          return (
            <div key={i} className="flex items-center justify-center">
              <span
                onClick={() => handleDateSelect(item.date)}
                className={`
                  w-8 h-8 flex flex-col items-center justify-center rounded-md cursor-pointer transition-all text-[11px] gap-[2px]
                  ${item.type !== 'current' ? 'text-gray-300' : isSunday ? 'text-red-500' : 'text-[#2c3e50] font-medium'}
                  ${isSelected ? 'border border-[#77b054] text-[#77b054] font-bold' : 'hover:bg-gray-200'}
                  ${isToday && !isSelected ? 'bg-[#77b054]/10 text-[#77b054] font-bold' : ''}
                `}
              >
                {item.day}
                {showDot ? (
                  <div className="w-[3px] h-[3px] rounded-full bg-[#77b054]" />
                ) : (
                  <div className="w-[3px] h-[3px]" />
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
